<template>
  <div v-if="visible" class="modal-overlay" @click.self="handleClose">
    <div class="modal-dialog-fullscreen">
      <!-- 标题栏 - 绿色基调 -->
      <div class="modal-header-bar">
        <h3 class="modal-title-text">{{ processName || '发起任务' }}</h3>
        <button class="modal-close-btn" @click="handleClose">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body-content">
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <span>加载表单中...</span>
        </div>

        <div v-else-if="error" class="error-state">
          <i class="fas fa-exclamation-circle"></i>
          <span>{{ error }}</span>
          <button class="btn btn-sm btn-primary" @click="loadMeta">重试</button>
        </div>

        <!-- 左右分栏布局，参考审批页面 -->
        <div v-else class="main-content-layout">
          <!-- 左侧: 表单区域 -->
          <div class="left-panel">
            <div class="form-section">
              <div class="form-content no-header">
                <!-- 新设计器表单 -->
                <NewFormRenderer
                  v-if="designerType === '2' && formControls.length > 0"
                  :controls="formControls"
                  :form-data="formState.formData"
                  @update="onFormDataUpdate"
                />

                <!-- 旧设计器表单 -->
                <OldFormRenderer
                  v-else-if="designerType !== '2' && formContent"
                  ref="oldFormRef"
                  :html-content="formContent"
                  :js-content="jsContent"
                  :form-data="formState.formData"
                  @update="onFormDataUpdate"
                />

                <div v-else class="empty-form-hint">
                  <i class="fas fa-file-alt"></i>
                  <span>该流程未配置表单</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧: 操作面板 -->
          <div class="right-panel">
            <!-- 基本信息 -->
            <div class="side-card">
              <div class="card-header">
                <i class="fas fa-info-circle"></i>
                <span>基本信息</span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label class="form-label">优先级</label>
                  <select v-model="formState.priority" class="form-select">
                    <option value="0">低</option>
                    <option value="1">中</option>
                    <option value="2">高</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">摘要</label>
                  <textarea
                    v-model="formState.summary"
                    class="form-textarea"
                    rows="3"
                    placeholder="请输入摘要信息..."
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- 流程说明 -->
            <div class="side-card" v-if="processRemark">
              <div class="card-header">
                <i class="fas fa-info-circle"></i>
                <span>流程说明</span>
              </div>
              <div class="card-body">
                <div class="node-description">{{ processRemark }}</div>
              </div>
            </div>

            <!-- 流程文档 -->
            <div class="side-card" v-if="flowDocs.length > 0">
              <div class="card-header">
                <i class="fas fa-file-upload"></i>
                <span>流程文档</span>
              </div>
              <div class="card-body">
                <div class="doc-item" v-for="doc in flowDocs" :key="doc.Guid || doc.guid">
                  <i :class="['fas', getDocIcon(doc.FileType || doc.fileType)]" style="color:#52c41a;margin-right:6px;"></i>
                  <a :href="doc.FileUrl || doc.fileUrl || '#' " target="_blank" class="doc-link">{{ doc.FileName || doc.fileName }}</a>
                </div>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="side-card">
              <div class="card-header">
                <i class="fas fa-tasks"></i>
                <span>操作</span>
              </div>
              <div class="card-body">
                <div class="action-buttons-vertical">
                  <button class="btn-action btn-submit" @click="handleSubmit" :disabled="submitting || loading">
                    <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-paper-plane"></i>
                    {{ submitting ? '提交中...' : '提交' }}
                  </button>
                  <button class="btn-action btn-save" @click="handleSaveDraft" :disabled="submitting || loading">
                    <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-save"></i>
                    {{ submitting ? '保存中...' : '保存草稿' }}
                  </button>
                  <button class="btn-action btn-cancel" @click="handleClose" :disabled="submitting">
                    <i class="fas fa-times"></i> 取消
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessageBox } from '../utils/messageBox'
import { getStartMeta, startTask, saveDraft, loadDraft } from '../api/task'
import { getFormById, getFormsByFlow } from '../api/form'
import { getAttachList } from '../api/attach'
import NewFormRenderer from './NewFormRenderer.vue'
import OldFormRenderer from './OldFormRenderer.vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  processId: { type: String, default: '' },
  processName: { type: String, default: '' },
  formGuid: { type: String, default: '' },
  designerType: { type: String, default: '2' },
  draftGuid: { type: String, default: '' }
})

const emit = defineEmits(['close', 'success'])

const loading = ref(false)
const submitting = ref(false)
const error = ref('')

const designerType = ref('2')
const formContent = ref('')
const jsContent = ref('')
const formControls = ref([])
const oldFormRef = ref(null)

// 右侧面板信息
const processRemark = ref('')
const flowDocs = ref([])

const formState = reactive({
  priority: '1',
  summary: '',
  formData: {}
})

const loadMeta = async () => {
  if (!props.processId) {
    error.value = '流程ID不能为空'
    return
  }

  loading.value = true
  error.value = ''
  processRemark.value = ''
  flowDocs.value = []

  try {
    let formGuid = props.formGuid || ''
    designerType.value = props.designerType || '2'

    // 流程列表项没有 formGuid，必须调用 getStartMeta 获取
    if (!formGuid) {
      try {
        const res = await getStartMeta(props.processId)
        console.log('[TaskLaunchModal] getStartMeta 返回:', JSON.stringify(res))
        const data = res.data || res.Data || res
        console.log('[TaskLaunchModal] 解析后 data:', JSON.stringify(data))
        formGuid = data.formGuid || data.FormGuid || ''
        designerType.value = data.designerType || data.DesignerType || '2'
        processRemark.value = data.remark || data.Remark || ''
      } catch (metaError) {
        console.warn('[TaskLaunchModal] getStartMeta 失败:', metaError)
      }
    }

    // 仍然没有 formGuid，降级到 getFormsByFlow
    if (!formGuid) {
      try {
        const formsRes = await getFormsByFlow(props.processId)
        const forms = formsRes.data || formsRes.Data || formsRes || []
        console.log('[TaskLaunchModal] getFormsByFlow 返回:', JSON.stringify(forms))
        if (forms.length > 0) {
          const firstForm = forms[0]
          formGuid = firstForm.guid || firstForm.Guid || firstForm.formGuid || firstForm.FormGuid || ''
          designerType.value = firstForm.designerType || firstForm.DesignerType || firstForm.type || firstForm.Type || '2'
        }
      } catch (formListError) {
        console.warn('[TaskLaunchModal] getFormsByFlow 也失败:', formListError)
      }
    }

    if (formGuid) {
      const formRes = await getFormById(formGuid)
      const formData = formRes.data || formRes.Data || formRes
      console.log('[TaskLaunchModal] getFormById 返回 formData keys:', Object.keys(formData))

      formContent.value = formData.formContent || formData.FormContent || ''
      jsContent.value = formData.jsContent || formData.JSContent || ''
      console.log('[TaskLaunchModal] formContent 长度:', formContent.value.length, 'jsContent 长度:', jsContent.value.length)
      console.log('[TaskLaunchModal] designerType:', designerType.value)
      console.log('[TaskLaunchModal] processRemark:', processRemark.value)
      console.log('[TaskLaunchModal] flowDocs 数量:', flowDocs.value.length)
      console.log('[TaskLaunchModal] formContent 前 500 字符:', formContent.value.substring(0, 500))

      if (designerType.value === '2' && formContent.value) {
        try {
          formControls.value = JSON.parse(formContent.value)
        } catch (e) {
          console.error('[TaskLaunchModal] 解析新设计器控件失败:', e)
          formControls.value = []
        }
      }

      // 加载草稿数据（如果有 draftGuid）
      if (props.draftGuid) {
        try {
          const draftRes = await loadDraft(props.draftGuid)
          const draft = draftRes.data || draftRes.Data || draftRes
          console.log('[TaskLaunchModal] 加载草稿数据:', draft)
          if (draft && draft.formData) {
            let parsed = null
            if (typeof draft.formData === 'string') {
              try { parsed = JSON.parse(draft.formData) } catch (e) { parsed = null }
            } else {
              parsed = draft.formData
            }
            if (parsed && typeof parsed === 'object') {
              formState.formData = { ...parsed }
            }
          }
          if (draft && draft.draftName) {
            formState.summary = draft.draftName
          }
        } catch (draftErr) {
          console.warn('[TaskLaunchModal] 加载草稿失败:', draftErr)
        }
      }
    } else {
      error.value = '该流程未配置表单，请联系管理员配置表单后再试。'
    }

    // 加载流程文档
    try {
      const docRes = await getAttachList('Flow', props.processId)
      const docData = docRes.data || docRes.Data || docRes
      flowDocs.value = (docData && (docData.data || docData.Data || docData)) || []
    } catch (e) {
      console.warn('[TaskLaunchModal] 加载流程文档失败:', e)
    }
  } catch (e) {
    console.error('[TaskLaunchModal] 加载表单失败:', e)
    error.value = e.message || '加载失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

watch(() => props.visible, (val) => {
  if (val) {
    loadMeta()
  } else {
    formState.priority = '1'
    formState.summary = ''
    formState.formData = {}
    formControls.value = []
    formContent.value = ''
    jsContent.value = ''
    error.value = ''
  }
})

const onFormDataUpdate = (data) => {
  if (!data) return
  // Handle nested format from NewFormRenderer: { type: 'main', table, field, value } or { detail: {...} }
  if (data.type === 'main' && data.table && data.field) {
    if (!formState.formData.main) formState.formData.main = {}
    if (!formState.formData.main[data.table]) formState.formData.main[data.table] = {}
    formState.formData.main[data.table][data.field] = data.value
    return
  }
  if (data.detail) {
    if (!formState.formData.detail) formState.formData.detail = {}
    Object.assign(formState.formData.detail, data.detail)
    return
  }
  // Flat format: { fieldName: value }
  Object.assign(formState.formData, data)
}

const getDocIcon = (fileType) => {
  if (!fileType) return 'fa-file'
  const t = (fileType + '').toLowerCase()
  if (t.includes('pdf')) return 'fa-file-pdf'
  if (t.includes('doc')) return 'fa-file-word'
  if (t.includes('xls') || t.includes('sheet')) return 'fa-file-excel'
  if (t.includes('ppt') || t.includes('presentation')) return 'fa-file-powerpoint'
  if (t.includes('zip') || t.includes('rar') || t.includes('7z')) return 'fa-file-archive'
  if (t.includes('img') || t.includes('jpg') || t.includes('png') || t.includes('gif')) return 'fa-file-image'
  return 'fa-file'
}

const getFieldValueForValidation = (ctrl) => {
  const p = ctrl.props || {}
  const fieldName = p.dataSourceField || p.fieldName || p.bindColumn || p.name || ctrl.id
  const tableName = p.dataTargetTable || p.dataSourceTable || ''
  // Try nested format
  if (tableName && formState.formData.main && formState.formData.main[tableName]) {
    const val = formState.formData.main[tableName][fieldName]
    if (val !== undefined && val !== '') return val
  }
  // Try flat format
  const flatVal = formState.formData[fieldName]
  if (flatVal !== undefined && flatVal !== '') return flatVal
  return ''
}

const validateForm = () => {
  if (designerType.value === '2') {
    const checkRequired = (controls) => {
      for (const ctrl of controls) {
        const p = ctrl.props || {}
        if (p.hidden) continue
        if (p.required && ctrl.type !== 'FileUpload' && ctrl.type !== 'ImageUpload') {
          const value = getFieldValueForValidation(ctrl)
          if (!value || value === '') {
            ElMessageBox.alert(`${p.label || '字段'} 不能为空`, '提示', { type: 'warning' })
            return false
          }
        }
        // InputNumber: 不可为负 / 不可为零 校验
        if (ctrl.type === 'InputNumber') {
          const value = getFieldValueForValidation(ctrl)
          if (value !== '' && value !== undefined) {
            const numVal = parseFloat(value)
            if (!isNaN(numVal)) {
              if (p.neg && numVal < 0) {
                ElMessageBox.alert(`${p.label || '字段'} 不能为负数`, '提示', { type: 'warning' })
                return false
              }
              if (p.notZero && numVal === 0) {
                ElMessageBox.alert(`${p.label || '字段'} 不能为零`, '提示', { type: 'warning' })
                return false
              }
            }
          }
        }
        // RichText: maxLength 校验
        if (ctrl.type === 'RichText' && p.maxLength) {
          const value = getFieldValueForValidation(ctrl)
          const plain = value ? String(value).replace(/<[^>]+>/g, '') : ''
          if (plain.length > p.maxLength) {
            ElMessageBox.alert(`${p.label || '字段'} 超出最大长度 ${p.maxLength}`, '提示', { type: 'warning' })
            return false
          }
        }
        // Check children (Container, Fieldset, Tabs)
        if (ctrl.children && ctrl.children.length > 0) {
          if (!checkRequired(ctrl.children)) return false
        }
        if (ctrl.type === 'Tabs' && p.tabs) {
          for (const tab of p.tabs) {
            if (tab.children && tab.children.length > 0) {
              if (!checkRequired(tab.children)) return false
            }
          }
        }
        // Check SubTable columns
        if (ctrl.type === 'SubTable' && p.columns) {
          for (const col of p.columns) {
            const cp = col.props || {}
            const tableName = (p.formbindtable || '').toLowerCase()
            const rows = formState.formData.detail?.[tableName] || []
            const fieldName = cp.dataSourceField || cp.fieldName || cp.bindColumn || col.id
            if (cp.required) {
              for (const row of rows) {
                if (!row[fieldName] || row[fieldName] === '') {
                  ElMessageBox.alert(`子表 ${p.label || ''} 中 ${cp.label || '字段'} 不能为空`, '提示', { type: 'warning' })
                  return false
                }
              }
            }
            // SubTable InputNumber: 不可为负 / 不可为零 校验
            if (col.type === 'InputNumber') {
              for (const row of rows) {
                const val = row[fieldName]
                if (val !== '' && val !== undefined && val !== null) {
                  const numVal = parseFloat(val)
                  if (!isNaN(numVal)) {
                    if (cp.neg && numVal < 0) {
                      ElMessageBox.alert(`子表 ${p.label || ''} 中 ${cp.label || '字段'} 不能为负数`, '提示', { type: 'warning' })
                      return false
                    }
                    if (cp.notZero && numVal === 0) {
                      ElMessageBox.alert(`子表 ${p.label || ''} 中 ${cp.label || '字段'} 不能为零`, '提示', { type: 'warning' })
                      return false
                    }
                  }
                }
              }
            }
          }
        }
      }
      return true
    }
    if (!checkRequired(formControls.value)) return false
  } else if (oldFormRef.value) {
    // ... existing old form validation
    const collected = oldFormRef.value.collectFormData()
    formState.formData = { ...formState.formData, ...collected }
    const container = oldFormRef.value.$el || oldFormRef.value
    if (container) {
      const requiredEls = container.querySelectorAll('[check="notnull"]')
      for (const el of requiredEls) {
        const name = el.getAttribute('name') || el.id
        const value = collected[name] || ''
        if (!value || value.trim() === '') {
          const title = el.getAttribute('checktitle') || el.getAttribute('title') || el.getAttribute('name') || '必填字段'
          ElMessageBox.alert(`${title} 不能为空`, '提示', { type: 'warning' })
          return false
        }
      }
    }
  }
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return
  if (designerType.value !== '2' && oldFormRef.value) {
    const collected = oldFormRef.value.collectFormData()
    formState.formData = { ...formState.formData, ...collected }
  }

  submitting.value = true
  try {
    const payload = {
      flowGuid: props.processId,
      formData: formState.formData,
      priority: parseInt(formState.priority),
      summary: formState.summary || ''
    }
    await startTask(payload)
    ElMessageBox.alert('任务发起成功！', '提示', { type: 'success' })
    emit('success')
    emit('close')
  } catch (e) {
    console.error('发起任务失败:', e)
    ElMessageBox.alert('发起任务失败: ' + (e.message || '未知错误'), '错误', { type: 'error' })
  } finally {
    submitting.value = false
  }
}

const handleSaveDraft = async () => {
  if (designerType.value !== '2' && oldFormRef.value) {
    const collected = oldFormRef.value.collectFormData()
    formState.formData = { ...formState.formData, ...collected }
  }

  submitting.value = true
  try {
    const payload = {
      flowGuid: props.processId,
      formData: formState.formData,
      priority: parseInt(formState.priority),
      summary: formState.summary || '',
      draftGuid: props.draftGuid
    }
    await saveDraft(payload)
    ElMessageBox.alert('草稿保存成功！', '提示', { type: 'success' })
  } catch (e) {
    console.error('保存草稿失败:', e)
    ElMessageBox.alert('保存草稿失败: ' + (e.message || '未知错误'), '错误', { type: 'error' })
  } finally {
    submitting.value = false
  }
}

const handleClose = () => {
  if (submitting.value) return
  emit('close')
}
</script>

<style scoped>
/* 全屏弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  padding: 12px;
}

.modal-dialog-fullscreen {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: calc(100vh - 24px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 标题栏 - 绿色基调 */
.modal-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-title-text {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.modal-close-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* 主体内容区 */
.modal-body-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
  color: #999;
  flex: 1;
}

.loading-state i,
.error-state i {
  font-size: 32px;
}

.error-state {
  color: #d9534f;
}

/* 左右分栏布局 */
.main-content-layout {
  flex: 1;
  display: flex;
  gap: 12px;
  padding: 12px;
  overflow: hidden;
}

/* 左侧面板 (75%) */
.left-panel {
  flex: 0 0 75%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* 右侧面板 (23%) */
.right-panel {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

/* 表单区域 */
.form-section {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #fff;
}

.form-content.no-header {
  padding: 0;
  background: transparent;
}

.empty-form-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 12px;
  color: #9ca3af;
}

.empty-form-hint i {
  font-size: 48px;
}

/* 右侧卡片 */
.side-card {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  flex-shrink: 0;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-bottom: 2px solid #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.card-header i {
  margin-right: 6px;
  color: #667eea;
  font-size: 12px;
}

.card-body {
  padding: 12px 14px;
}

/* 表单组 */
.form-group {
  margin-bottom: 10px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.form-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  background: #fff;
  font-family: inherit;
  box-sizing: border-box;
}

.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  background: #fff;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
}

.form-textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* 按钮 */
.action-buttons-vertical {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-action {
  padding: 8px 14px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.btn-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-submit {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.btn-save {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.btn-cancel {
  background: #fff;
  color: #666;
  border: 1px solid #d9d9d9;
}

.btn-cancel:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

/* 滚动条美化 */
.form-content::-webkit-scrollbar,
.right-panel::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.form-content::-webkit-scrollbar-track,
.right-panel::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.form-content::-webkit-scrollbar-thumb,
.right-panel::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.form-content::-webkit-scrollbar-thumb:hover,
.right-panel::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 节点说明 */
.node-description {
  padding: 8px 10px;
  color: #374151;
  font-size: 12px;
  line-height: 1.5;
}

/* 文档列表 */
.doc-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 12px;
}

.doc-link {
  color: #1890ff;
  text-decoration: none;
  word-break: break-all;
}

.doc-link:hover {
  text-decoration: underline;
}

/* 响应式 */
@media (max-width: 1200px) {
  .main-content-layout {
    flex-direction: column;
  }
  .left-panel,
  .right-panel {
    flex: 1 1 auto;
    width: 100%;
  }
  .right-panel {
    max-height: 400px;
  }
}
</style>
